import { RestClient } from '@libs/rest-client';
import { useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface CompanyIdentity {
  companyCode: string;
  companyName: string;
}

interface AiCompanyContextProps {
  companyCode: string;
  companyName: string;
  fetchContext: () => Promise<void>;
}

const AiCompanyContext = createContext<AiCompanyContextProps>({
  companyCode: '',
  companyName: '',
  fetchContext: async () => {},
});

export function AiCompanyProvider({ children }: { children: ReactNode }) {
  const [company, setCompany] = useState<CompanyIdentity | null>(null);
  const { tryRequest } = useTry();

  const fetchContext = useCallback(async () => {
    await tryRequest(async () => {
      const companyResponse = await RestClient.fa.get<{ company: CompanyIdentity }>('/company/context');
      setCompany(companyResponse.company);
    });
  }, []);

  useEffect(() => {
    void fetchContext();
  }, [fetchContext]);

  if (!company) {
    return null;
  }

  return (
    <AiCompanyContext.Provider
      value={{
        companyCode: company.companyCode,
        companyName: company.companyName,
        fetchContext,
      }}
    >
      {children}
    </AiCompanyContext.Provider>
  );
}

export function useAiCompany() {
  return useContext(AiCompanyContext);
}
